import React from 'react';
import { type Header, flexRender } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, Filter } from 'lucide-react';
import { HeaderFilter } from './HeaderFilter';
import { isFilterActive } from './headerFilterState';
import { type Pin, pinCellProps } from './pinning';
import type { HeaderFilterConfig } from './types';

const SortIcon: React.FC<{ sorted: false | 'asc' | 'desc' }> = ({ sorted }) =>
  sorted === 'asc' ? (
    <ArrowUp className='h-3 w-3 text-primary' />
  ) : sorted === 'desc' ? (
    <ArrowDown className='h-3 w-3 text-primary' />
  ) : (
    <ArrowUpDown className='h-3 w-3 opacity-40' />
  );

/**
 * A column's header: its title, a sort button when the column sorts, and its
 * checklist filter when it has one.
 */
export function HeaderCell<T>({
  header,
  pin,
  filter,
  onSort,
}: {
  header: Header<T, unknown>;
  pin: Pin;
  filter?: HeaderFilterConfig;
  /** A server sort's header click; without it the grid sorts the column itself. */
  onSort?: (columnId: string) => void;
}) {
  const { column } = header;
  const sorted = column.getIsSorted();
  const filterActive = isFilterActive(filter);
  const pinned = pinCellProps(pin, header.getSize(), { background: false });
  const title = flexRender(column.columnDef.header, header.getContext());
  const label =
    typeof column.columnDef.header === 'string'
      ? column.columnDef.header
      : column.id;
  return (
    <th
      aria-sort={
        !column.getCanSort()
          ? undefined
          : sorted === 'asc'
            ? 'ascending'
            : sorted === 'desc'
              ? 'descending'
              : 'none'
      }
      // The header sticks to the top of the scrolling rows; a pinned header
      // cell sits above both.
      style={{
        ...pinned.style,
        position: 'sticky',
        top: 0,
        zIndex: pin ? 30 : 20,
      }}
      className={`h-9 border-b border-r border-b-slate-200 border-r-slate-100 px-2 text-left text-xs font-medium text-slate-600 last:border-r-0 ${pinned.className} ${filterActive ? 'bg-[color-mix(in_srgb,hsl(var(--primary))_8%,white)]' : 'bg-white'}`}
    >
      <div
        className={`flex items-center gap-1 ${column.columnDef.meta?.headerAlign === 'center' ? 'justify-center' : ''}`}
      >
        {column.getCanSort() ? (
          <button
            type='button'
            className='flex items-center gap-1 hover:text-foreground'
            onClick={
              onSort
                ? () => onSort(column.id)
                : column.getToggleSortingHandler()
            }
          >
            {title}
            <SortIcon sorted={sorted} />
          </button>
        ) : (
          <span className='flex min-w-0 items-center gap-1'>
            <span className='truncate'>{title}</span>
          </span>
        )}
        {filter && (
          <HeaderFilter
            iconTrigger={
              <Filter
                className={`h-3 w-3 ${filterActive ? 'fill-primary text-primary' : 'opacity-40 group-hover:opacity-100'}`}
              />
            }
            options={filter.options}
            value={filter.selected}
            onChange={filter.onChange}
            ariaLabel={`Filter ${label}`}
          />
        )}
      </div>
    </th>
  );
}
